import { api } from './api';
import type { User } from '@/types';

interface LoginResponse {
  data: User | null;
}

export interface SignInRequest {
  email: string;
  password: string;
}

export interface MfaSetupResponse {
  secret: string;
  keyUri: string;
}

export interface MfaVerifyRequest {
  code: string;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
  code: string;
}

export const authApi = api.injectEndpoints({
  endpoints: (builder) => ({
    login: builder.mutation<LoginResponse, SignInRequest>({
      query: (credentials) => ({
        url: '/auth',
        method: 'POST',
        body: credentials,
      }),
      invalidatesTags: ['User'],
    }),

    getCurrentUser: builder.query<User | null, void>({
      query: () => ({
        url: '/users/me',
      }),
      providesTags: ['User'],
    }),

    logout: builder.mutation<void, void>({
      query: () => ({
        url: '/auth',
        method: 'DELETE',
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(api.util.resetApiState());
        } catch {
          console.error('Failed to logout');
        }
      },
    }),

    refreshToken: builder.mutation<{ message: string }, void>({
      query: () => ({
        url: '/auth',
        method: 'PUT',
      }),
    }),

    initializeMfaSetup: builder.query<MfaSetupResponse, void>({
      query: () => ({
        url: '/auth/mfa',
        method: 'POST',
      }),
    }),

    verifyMfaSetup: builder.mutation<{ message: string }, MfaVerifyRequest>({
      query: (data) => ({
        url: '/auth/mfa',
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['User'],
    }),

    verifyMfa: builder.mutation<{ message: string }, MfaVerifyRequest>({
      query: (data) => ({
        url: '/auth',
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['User'],
    }),

    changePassword: builder.mutation<{ message: string }, ChangePasswordRequest>({
      query: (data) => ({
        url: '/users/me',
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['User'],
    }),

    impersonateUser: builder.mutation<User, string>({
      query: (targetUserId) => ({
        url: `/auth/${targetUserId}`,
        method: 'POST',
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(api.util.resetApiState());
        } catch {
          console.error('Failed to impersonate user');
        }
      },
    }),
  }),
});

export const {
  useLoginMutation,
  useLogoutMutation,
  useGetCurrentUserQuery,
  useRefreshTokenMutation,
  useInitializeMfaSetupQuery,
  useVerifyMfaSetupMutation,
  useVerifyMfaMutation,
  useChangePasswordMutation,
  useImpersonateUserMutation,
} = authApi;

export const {
  endpoints: { login, logout, getCurrentUser, refreshToken },
} = authApi;
